import React, { useState } from 'react';
import {
  X,
  ExternalLink,
  Star,
  Calendar,
  Building,
  MapPin,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  FileText,
  Tag,
  ShieldCheck,
  Send,
  MessageSquare,
  History,
  Layers,
  Sparkles,
  ArrowRight,
  Edit3,
  ShieldAlert,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
  updateTenderCommercialStatus,
  toggleTenderFavorite,
  addTenderInternalNote,
  submitTenderAdminFeedback,
} from '../../../services/tenderAgentApi';

export default function TenderDetailModal({ tender, onClose, onUpdated }) {
  if (!tender) return null;

  const [currentTender, setCurrentTender] = useState(tender);
  const [internalStatus, setInternalStatus] = useState(tender.internalStatus || 'NEW');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, lots, history, notes, feedback

  // Feedback form
  const [feedbackScore, setFeedbackScore] = useState(tender.aiScore || 50);
  const [feedbackTier, setFeedbackTier] = useState(tender.aiTier || 'UNCERTAIN');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await updateTenderCommercialStatus(currentTender._id, { internalStatus: newStatus });
      if (res?.success) {
        setInternalStatus(newStatus);
        setCurrentTender((prev) => ({ ...prev, internalStatus: newStatus }));
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Impossible de mettre à jour le statut.',
      });
    }
  };

  const handleToggleFav = async () => {
    try {
      const res = await toggleTenderFavorite(currentTender._id);
      if (res?.success) {
        const nextFav = res.data?.isFavorite;
        setCurrentTender((prev) => ({ ...prev, isFavorite: nextFav }));
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Erreur toggle favori:', err);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    try {
      setSavingNote(true);
      const res = await addTenderInternalNote(currentTender._id, noteText);
      if (res?.success) {
        const newNotes = res.data?.internalNotes || [
          ...(currentTender.internalNotes || []),
          { noteText, createdAt: new Date().toISOString() },
        ];
        setCurrentTender((prev) => ({ ...prev, internalNotes: newNotes }));
        setNoteText('');
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Impossible d\'ajouter la note.',
      });
    } finally {
      setSavingNote(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    try {
      setSavingFeedback(true);
      const res = await submitTenderAdminFeedback(currentTender._id, {
        adjustedScore: Number(feedbackScore),
        adjustedTier: feedbackTier,
        feedbackNotes,
      });

      if (res?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Feedback enregistré',
          text: 'Votre correction a été enregistrée pour affiner les futurs classements.',
          timer: 2000,
          showConfirmButton: false,
        });
        setCurrentTender((prev) => ({
          ...prev,
          aiScore: Number(feedbackScore),
          aiTier: feedbackTier,
          adminFeedback: {
            adjustedScore: Number(feedbackScore),
            adjustedTier: feedbackTier,
            notes: feedbackNotes,
            submittedAt: new Date(),
          },
        }));
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Impossible d\'enregistrer le feedback.',
      });
    } finally {
      setSavingFeedback(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non précisée';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  const getDeadlineBadge = (deadlineStr) => {
    if (!deadlineStr) return null;
    const now = new Date();
    const deadline = new Date(deadlineStr);
    const diffDays = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <span className="ao-badge ao-badge-closed">Clôturé ({Math.abs(diffDays)} j passés)</span>;
    }
    if (diffDays <= 3) {
      return <span className="ao-badge ao-badge-urgent">⚠️ Urgent : J-{diffDays}</span>;
    }
    if (diffDays <= 7) {
      return <span className="ao-badge ao-badge-warning">Imminent : J-{diffDays}</span>;
    }
    return <span className="ao-badge ao-badge-success">{diffDays} jours restants</span>;
  };

  const getScoreBadge = (score, tier) => {
    if (tier === 'HIGH_RELEVANCE' || score >= 70) {
      return <span className="ao-tier-badge high">Forte Pertinence ({score}/100)</span>;
    }
    if (tier === 'MEDIUM_RELEVANCE' || score >= 50) {
      return <span className="ao-tier-badge medium">Pertinence Moyenne ({score}/100)</span>;
    }
    if (tier === 'UNCERTAIN') {
      return <span className="ao-tier-badge uncertain">À Examiner ({score}/100)</span>;
    }
    return <span className="ao-tier-badge low">Faible Pertinence ({score}/100)</span>;
  };

  return (
    <div className="ao-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ao-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* ─── Modal Header ─── */}
        <div className="ao-modal-header">
          <div className="ao-modal-header-left">
            <button
              type="button"
              className={`ao-star-btn-lg ${currentTender.isFavorite ? 'active' : ''}`}
              onClick={handleToggleFav}
              title={currentTender.isFavorite ? 'Retirer des favoris' : 'Marquer comme favori'}
            >
              <Star size={22} className={currentTender.isFavorite ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
            </button>
            <div>
              <div className="ao-modal-tags-row">
                <span className="ao-bid-number">N° {currentTender.bidNo}</span>
                {currentTender.bidModSeq && currentTender.bidModSeq !== '00' && (
                  <span className="ao-badge ao-badge-warning">
                    Rectificatif #{currentTender.bidModSeq}
                  </span>
                )}
                <span className="ao-source-badge">{currentTender.source || 'TUNEPS'}</span>
                <span className="ao-type-badge">{currentTender.tenderType || 'AO'}</span>
              </div>
              <h2 className="ao-modal-title">{currentTender.title}</h2>
              {currentTender.titleAr && currentTender.titleAr !== currentTender.title && (
                <p className="ao-arabic-title" dir="rtl">
                  {currentTender.titleAr}
                </p>
              )}
            </div>
          </div>

          <div className="ao-modal-header-right">
            {/* Statut commercial dropdown */}
            <select
              className={`ao-status-select-pill ${internalStatus.toLowerCase()}`}
              value={internalStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              title="Modifier le statut commercial interne"
            >
              <option value="NEW">Nouveau</option>
              <option value="TO_REVIEW">À examiner</option>
              <option value="INTERESTED">Intéressé</option>
              <option value="PREPARING">En préparation</option>
              <option value="SUBMITTED">Soumissionné</option>
              <option value="WON">Gagné</option>
              <option value="LOST">Perdu</option>
              <option value="NOT_INTERESTED">Sans suite</option>
            </select>

            <button type="button" className="ao-modal-close-btn" onClick={onClose} aria-label="Fermer la modale">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ─── Navigation par Onglets ─── */}
        <div className="ao-modal-tabs">
          <button
            type="button"
            className={`ao-modal-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Sparkles size={16} />
            <span>Synthèse & Pertinence IA</span>
          </button>
          <button
            type="button"
            className={`ao-modal-tab-btn ${activeTab === 'lots' ? 'active' : ''}`}
            onClick={() => setActiveTab('lots')}
          >
            <Layers size={16} />
            <span>Lots & Conditions ({currentTender.lots?.length || 0})</span>
          </button>
          <button
            type="button"
            className={`ao-modal-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <History size={16} />
            <span>Historique & Reports ({currentTender.changeHistory?.length || 0})</span>
          </button>
          <button
            type="button"
            className={`ao-modal-tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <MessageSquare size={16} />
            <span>Notes Internes ({currentTender.internalNotes?.length || 0})</span>
          </button>
          <button
            type="button"
            className={`ao-modal-tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback')}
          >
            <Edit3 size={16} />
            <span>Feedback IA</span>
          </button>
        </div>

        {/* ─── Corps de la Modale ─── */}
        <div className="ao-modal-body">
          {/* ──── TAB 1 : SYNTHÈSE & PERTINENCE IA ──── */}
          {activeTab === 'overview' && (
            <div className="ao-tab-pane">
              {/* Carte Score & Synthèse IA */}
              <div className="ao-ai-card">
                <div className="ao-ai-card-header">
                  <div className="flex items-center space-x-2">
                    <Sparkles size={18} className="text-amber-500" />
                    <span className="font-bold text-gray-900 text-sm">Évaluation Multidimensionnelle de Pertinence</span>
                  </div>
                  {getScoreBadge(currentTender.aiScore, currentTender.aiTier)}
                </div>

                <div className="ao-ai-card-content">
                  <div className="ao-score-row">
                    <div className="ao-score-gauge">
                      <span className="ao-score-num">{currentTender.aiScore || 0}</span>
                      <span className="ao-score-denom">/100</span>
                    </div>
                    <div className="ao-score-details">
                      <h4 className="ao-subheading">Résumé IA du Marché</h4>
                      <p className="ao-prose">
                        {currentTender.aiSummary || 'Aucun résumé disponible pour ce marché.'}
                      </p>

                      <h4 className="ao-subheading mt-3">Justification Technique</h4>
                      <p className="ao-prose-muted">
                        {currentTender.aiJustification || 'Aucune justification détaillée enregistrée.'}
                      </p>
                    </div>
                  </div>

                  {/* Équipements solaires / électriques détectés */}
                  {currentTender.aiDetectedEquipments?.length > 0 && (
                    <div className="ao-equipment-section">
                      <div className="ao-equipment-label">Équipements & Prestations Détectés :</div>
                      <div className="ao-equipment-chips">
                        {currentTender.aiDetectedEquipments.map((eq, idx) => (
                          <span key={idx} className="ao-equipment-chip">
                            <Tag size={12} className="text-amber-600 mr-1" />
                            <span>{eq}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Éligibilité administrative vs commerciale */}
                  <div className="ao-eligibility-box">
                    <ShieldCheck size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed">
                      <strong className="text-amber-900 block font-semibold mb-0.5">
                        Éligibilité Administrative : {currentTender.aiEligibilityAssessment || 'Non vérifiée'}
                      </strong>
                      <span className="text-amber-800">
                        * Note méthodologique : Une forte adéquation technique n'induit pas automatiquement l'éligibilité légale ou la détention des agréments ministériels requis.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Spécifications administratives du marché */}
              <div className="ao-specs-grid">
                <div className="ao-spec-item">
                  <span className="ao-spec-label">Acheteur Public</span>
                  <span className="ao-spec-val flex items-center">
                    <Building size={14} className="text-gray-400 mr-1.5 flex-shrink-0" />
                    <span>{currentTender.buyer || 'Non précisé'}</span>
                  </span>
                </div>

                <div className="ao-spec-item">
                  <span className="ao-spec-label">Région / Gouvernorat</span>
                  <span className="ao-spec-val flex items-center">
                    <MapPin size={14} className="text-gray-400 mr-1.5 flex-shrink-0" />
                    <span>{currentTender.region || 'Toute la Tunisie'}</span>
                  </span>
                </div>

                <div className="ao-spec-item">
                  <span className="ao-spec-label">Date Limite de Soumission</span>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="ao-spec-val flex items-center">
                      <Clock size={14} className="text-gray-400 mr-1.5 flex-shrink-0" />
                      <span>{formatDate(currentTender.submissionDeadline)}</span>
                    </span>
                    {getDeadlineBadge(currentTender.submissionDeadline)}
                  </div>
                </div>

                <div className="ao-spec-item">
                  <span className="ao-spec-label">Budget Estimatif</span>
                  <span className="ao-spec-val font-bold text-emerald-700 flex items-center">
                    <DollarSign size={14} className="text-emerald-600 mr-1 flex-shrink-0" />
                    <span>
                      {currentTender.estimatedBudget
                        ? `${Number(currentTender.estimatedBudget).toLocaleString()} ${currentTender.currency || 'TND'}`
                        : 'Non communiqué (estimation confidentielle)'}
                    </span>
                  </span>
                </div>

                <div className="ao-spec-item">
                  <span className="ao-spec-label">Date de Publication</span>
                  <span className="ao-spec-val flex items-center">
                    <Calendar size={14} className="text-gray-400 mr-1.5 flex-shrink-0" />
                    <span>{formatDate(currentTender.publicationDate)}</span>
                  </span>
                </div>

                <div className="ao-spec-item">
                  <span className="ao-spec-label">Statut Officiel TUNEPS</span>
                  <span className="ao-spec-val font-semibold text-gray-800">
                    {currentTender.officialStatus || 'OPEN'}
                  </span>
                </div>
              </div>

              {/* Lien direct officiel TUNEPS */}
              {currentTender.sourceUrl && (
                <div className="ao-external-link-row">
                  <a
                    href={currentTender.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ao-external-btn"
                  >
                    <span>Consulter l'avis officiel et télécharger le cahier des charges sur TUNEPS</span>
                    <ExternalLink size={16} className="ml-2" />
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ──── TAB 2 : LOTS & CONDITIONS ──── */}
          {activeTab === 'lots' && (
            <div className="ao-tab-pane">
              <div>
                <h4 className="ao-section-title">
                  <Layers size={18} className="text-amber-500 mr-2" />
                  Lots Composant le Marché ({currentTender.lots?.length || 0})
                </h4>
                {currentTender.lots?.length > 0 ? (
                  <div className="ao-lots-list">
                    {currentTender.lots.map((lot, idx) => (
                      <div key={idx} className="ao-lot-card">
                        <div className="ao-lot-header">
                          <span className="ao-lot-badge">Lot N° {lot.lotNumber || idx + 1}</span>
                          {lot.budgetAmount && (
                            <span className="ao-lot-budget">
                              {Number(lot.budgetAmount).toLocaleString()} {lot.budgetCurrency || 'TND'}
                            </span>
                          )}
                        </div>
                        <h5 className="ao-lot-title">{lot.title}</h5>
                        {lot.description && <p className="ao-lot-desc">{lot.description}</p>}
                        {lot.guaranteeAmount && (
                          <div className="ao-lot-guarantee">
                            Cautionnement provisoire : {Number(lot.guaranteeAmount).toLocaleString()} {lot.guaranteeCurrency || 'TND'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ao-empty-box">
                    <p>Marché unique (sans décomposition en lots déclarée sur le portail).</p>
                  </div>
                )}
              </div>

              <div className="ao-conditions-wrapper">
                <h4 className="ao-section-title">
                  <ShieldCheck size={18} className="text-indigo-500 mr-2" />
                  Conditions de Participation & Pièces Exigées
                </h4>
                {currentTender.participationConditions?.length > 0 ? (
                  <div className="ao-conditions-list">
                    {currentTender.participationConditions.map((cond, idx) => (
                      <div key={idx} className="ao-condition-item">
                        <div className="ao-condition-label">
                          <strong>{cond.label || cond.conditionType}</strong>
                          {cond.mandatory && <span className="ao-badge-mandatory">Obligatoire</span>}
                        </div>
                        {cond.value && <div className="ao-condition-val">{cond.value}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ao-empty-box">
                    <p>Consulter le cahier des charges officiel pour les clauses administratives particulières.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ──── TAB 3 : HISTORIQUE DES MODIFICATIONS & REPORTS ──── */}
          {activeTab === 'history' && (
            <div className="ao-tab-pane">
              <h4 className="ao-section-title">
                <History size={18} className="text-amber-500 mr-2" />
                Journal des Détections, Rectificatifs & Reports de Délais
              </h4>
              {currentTender.changeHistory?.length > 0 ? (
                <div className="ao-timeline">
                  {currentTender.changeHistory.map((item, idx) => (
                    <div key={idx} className="ao-timeline-item">
                      <div className="ao-timeline-marker" />
                      <div className="ao-timeline-content">
                        <div className="ao-timeline-header">
                          <span className="ao-timeline-summary">{item.summary || item.changeType}</span>
                          <span className="ao-timeline-time">{formatDate(item.detectedAt)}</span>
                        </div>
                        {item.changeType === 'DEADLINE_POSTPONED' && (
                          <div className="ao-timeline-postponed-callout">
                            Report officiel : {formatDate(item.previousValue)} → {formatDate(item.newValue)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ao-empty-box">
                  <p>Aucun avis modificatif officiel ni report de date détecté pour ce marché à ce jour.</p>
                </div>
              )}
            </div>
          )}

          {/* ──── TAB 4 : NOTES INTERNES ──── */}
          {activeTab === 'notes' && (
            <div className="ao-tab-pane">
              <h4 className="ao-section-title">
                <MessageSquare size={18} className="text-amber-500 mr-2" />
                Notes Commerciales & Suivi Interne
              </h4>

              {/* Formulaire ajout note */}
              <form onSubmit={handleAddNote} className="ao-notes-form">
                <textarea
                  className="ao-textarea"
                  rows={3}
                  placeholder="Rédiger une observation confidentielle (ex: étude de rentabilité, contact acheteur, demande de prix onduleur)..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  disabled={savingNote}
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="submit"
                    className="ao-btn ao-btn-primary"
                    disabled={savingNote || !noteText.trim()}
                  >
                    <Send size={14} className="mr-1.5" />
                    <span>Enregistrer la note</span>
                  </button>
                </div>
              </form>

              {/* Liste des notes */}
              <div className="ao-notes-list">
                {currentTender.internalNotes?.length > 0 ? (
                  currentTender.internalNotes.map((n, idx) => (
                    <div key={idx} className="ao-note-card">
                      <div className="ao-note-meta">
                        <span className="font-semibold text-gray-800">{n.authorEmail || 'Administrateur'}</span>
                        <span>{formatDate(n.createdAt)}</span>
                      </div>
                      <p className="ao-note-body">{n.noteText}</p>
                    </div>
                  ))
                ) : (
                  <div className="ao-empty-box">
                    <p>Aucune note confidentielle enregistrée sur cette opportunité.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ──── TAB 5 : FEEDBACK ADMINISTRATEUR ──── */}
          {activeTab === 'feedback' && (
            <div className="ao-tab-pane">
              <div className="ao-feedback-notice">
                <h5 className="font-semibold text-blue-900 mb-1">Affinement Continu de la Détection IA</h5>
                <p className="text-blue-800 text-xs leading-relaxed">
                  Si l'évaluation automatique est perfectible (surestimation ou omission d'une opportunité photovoltaïque), vous pouvez ajuster le score et la classification ci-dessous.
                </p>
              </div>

              <form onSubmit={handleSubmitFeedback} className="ao-feedback-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="ao-form-label">Score Révisé (0 à 100) :</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="ao-input"
                      value={feedbackScore}
                      onChange={(e) => setFeedbackScore(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="ao-form-label">Tier Révisé :</label>
                    <select
                      className="ao-select"
                      value={feedbackTier}
                      onChange={(e) => setFeedbackTier(e.target.value)}
                    >
                      <option value="HIGH_RELEVANCE">HIGH_RELEVANCE (Forte pertinence)</option>
                      <option value="MEDIUM_RELEVANCE">MEDIUM_RELEVANCE (Pertinence moyenne)</option>
                      <option value="UNCERTAIN">UNCERTAIN (À examiner)</option>
                      <option value="LOW_RELEVANCE">LOW_RELEVANCE (Faible pertinence)</option>
                      <option value="NOT_RELEVANT">NOT_RELEVANT (Non pertinent)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="ao-form-label">Commentaire Explicatif :</label>
                  <textarea
                    className="ao-textarea"
                    rows={3}
                    placeholder="Expliquez la raison de la correction administrative..."
                    value={feedbackNotes}
                    onChange={(e) => setFeedbackNotes(e.target.value)}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="ao-btn ao-btn-primary"
                    disabled={savingFeedback}
                  >
                    <CheckCircle2 size={16} className="mr-1.5" />
                    <span>Valider la correction</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
